import { Habit, Task, TaskType } from '@griply/types';
import {
  useArchiveTask,
  useCreateHabitAllowed,
  useDeleteTask,
  useDuplicateTasks,
} from 'features/tasks';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArchiveDocumentsBoxBig16,
  CopyDuplicateObject16,
  Trash16,
} from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import {
  Colors,
  PopupMenuButton,
  PopupMenuList,
  PopupMenuListItem,
} from 'shared/components/ui/popup-menu';
import { useOpenDeleteHabitDialog } from 'shared/contexts/delete-habit';
import { useOpenPremiumDialog } from 'shared/contexts/premium-dialog';

export type RemoveListProps = {
  task: Task;
  onClose: () => void;
  hasListBelow?: boolean;
  onAction?: () => void;
  onDelete?: () => void;
  onDuplicate?: () => void;
};

export const LifecycleList: React.FC<RemoveListProps> = ({
  task,
  onClose,
  hasListBelow,
  onAction,
  onDelete: customOnDelete,
  onDuplicate: customOnDuplicate,
}) => {
  const { t } = useTranslation();
  const archiveTask = useArchiveTask();
  const isCreateHabitAllowed = useCreateHabitAllowed();
  const openPremiumDialog = useOpenPremiumDialog();

  const parentTaskId = useMemo(
    () => task.parentIds?.[task.parentIds.length - 1],
    [task],
  );

  const duplicateTask = useDuplicateTasks(parentTaskId);
  const { submit: deleteTask } = useDeleteTask(parentTaskId);
  const deleteHabit = useOpenDeleteHabitDialog();

  const isHabit = [TaskType.Habit, TaskType.Repeating].includes(task.type);
  const isDuplicateAllowed = isCreateHabitAllowed.allowed || !isHabit;

  const onDuplicate = () => {
    if (customOnDuplicate) {
      customOnDuplicate();
      onAction?.();
      onClose();
      return;
    }

    if (!isDuplicateAllowed) {
      openPremiumDialog();
      onClose();
      return;
    }

    duplicateTask([task]);
    onAction?.();
    onClose();
  };

  const onArchive = () => {
    archiveTask(task);
    onAction?.();
    onClose();
  };

  const onDelete = () => {
    if (customOnDelete) {
      customOnDelete();
      onAction?.();
      onClose();
      return;
    }

    if (isHabit) {
      deleteHabit(task as Habit);
      onAction?.();
      onClose();
      return;
    }

    deleteTask(task);
    onAction?.();
    onClose();
  };

  return (
    <PopupMenuList hasBottomBorder={hasListBelow}>
      {!isHabit && (
        <PopupMenuListItem>
          <PopupMenuButton
            onClick={onDuplicate}
            start={<Icon icon={CopyDuplicateObject16} />}
          >
            {t('context-menus.task.labels.duplicate')}
          </PopupMenuButton>
        </PopupMenuListItem>
      )}
      {isHabit && (
        <PopupMenuListItem>
          <PopupMenuButton
            onClick={onArchive}
            start={<Icon icon={ArchiveDocumentsBoxBig16} />}
          >
            {t(
              task.archivedAt
                ? 'context-menus.task.labels.de-archive'
                : 'context-menus.task.labels.archive',
            )}
          </PopupMenuButton>
        </PopupMenuListItem>
      )}
      <PopupMenuListItem>
        <PopupMenuButton
          onClick={onDelete}
          start={<Icon icon={Trash16} />}
          startColor={Colors.Error}
          textColor={Colors.Error}
        >
          {t('context-menus.task.labels.delete')}
        </PopupMenuButton>
      </PopupMenuListItem>
    </PopupMenuList>
  );
};
